/* ===========================================================
   PRODUCT DETAIL PAGE
   =========================================================== */
import React from "react";
import { ProductCard, ProductTile, Quantity, STOCK_PILL, StarRating } from "@/components/product-card";
import { ReviewForm } from "@/components/review-form";
import { ReviewList } from "@/components/review-list";
import { Breadcrumbs, getQuery, toast } from "@/components/shell";
import { NETHRA_DATA, inr } from "@/data/data";
import { Icons } from "@/lib/icons";
import { useProduct, useProducts, useReviews, useAddCartItem } from "@/api/hooks";
import { useWishlist } from "@/hooks/useWishlist";
import { useNavigate } from "react-router-dom";

const { useState: pdpUS, useMemo: pdpUM } = React;

function PDPGallery({ product }) {
  const [active, setActive] = pdpUS(0);
  // 4 angles — same tile shape, swapped per slot
  return (
    <div className="pdp-gallery">
      <div className="pdp-main-img">
        <ProductTile product={product} size="lg" />
        {product.badge && <span className="prod-badge mono" style={{ top: 16, left: 16 }}>{product.badge}</span>}
      </div>
      <div className="pdp-thumbs">
        {[0, 1, 2, 3].map((i) => (
          <button key={i}
                  className={"pdp-thumb" + (active === i ? " is-active" : "")}
                  onClick={() => setActive(i)}>
            <ProductTile product={product} size="sm" />
          </button>
        ))}
      </div>
    </div>
  );
}

function PDPInfo({ product }) {
  const [variant, setVariant] = pdpUS(0);
  const [qty, setQty] = pdpUS(1);
  const stock = STOCK_PILL[product.stock_status];
  const addToCart = useAddCartItem();
  const wishlist = useWishlist();
  const wished = wishlist.has(product.slug);
  const navigate = useNavigate();
  const handleAdd = () => {
    addToCart.mutate({ slug: product.slug, qty }, {
      onSuccess: () => toast && toast(`Added ${qty} to cart`),
      onError: (err) => toast && toast(err?.message || "Couldn't add to cart", "error"),
    });
  };
  const handleBuyNow = () => {
    addToCart.mutate({ slug: product.slug, qty }, {
      onSuccess: () => navigate("/pages/storefront/cart.html"),
      onError: (err) => toast && toast(err?.message || "Couldn't add to cart", "error"),
    });
  };
  const toggleWish = () => {
    wishlist.toggle(product.slug);
    toast && toast(wished ? "Removed from wishlist" : "Saved to wishlist");
  };
  const variants = [
    { label: product.unit_label, sub: "Default pack", price: product.price_min },
    { label: "10 × " + product.unit_label.split(" ")[0], sub: "Bulk pack", price: Math.round(product.price_min * 9.4) },
  ];
  return (
    <div className="pdp-info">
      <div className="pdp-eyebrow">
        <span className={"pill " + stock.cls}>{stock.label}</span>
        <span className="pill pill-brand">{product.category_name}</span>
        {product.schedule && <span className="pill pill-outline">Schedule {product.schedule}</span>}
        {product.delivery_time_mins && (
          <span className="pill pill-outline"><Icons.Clock size={11} stroke={1.8} /> {product.delivery_time_mins} min dispatch</span>
        )}
      </div>

      <h1 className="pdp-title">{product.name}</h1>
      <div className="pdp-brand-row">
        <span>By <a href="#" className="pdp-brand-link">{product.brand}</a></span>
        <span style={{ color: "var(--line-2)" }}>•</span>
        <StarRating value={product.rating} count={product.reviews} size={13} />
        <span style={{ color: "var(--line-2)" }}>•</span>
        <span>SKU <span className="mono">{product.id.toUpperCase()}</span></span>
      </div>

      <div className="pdp-price-card">
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          <div className="pdp-price">{inr(variants[variant].price)}</div>
          <div className="kicker">per {variants[variant].label}</div>
        </div>
        <div className="pdp-price-sub">Includes GST. Role-based pricing — sign in for retailer or hospital tiers.</div>
        <div className="pdp-price-table">
          <div className="pdp-pt-cell">
            <div className="pdp-pt-k">MRP</div>
            <div className="pdp-pt-v">{inr(Math.round(product.price_max * 1.18))}</div>
          </div>
          <div className="pdp-pt-cell">
            <div className="pdp-pt-k">Retailer</div>
            <div className="pdp-pt-v" style={{ color: "#0a0a0a" }}>{inr(Math.round(product.price_min * 0.82))}</div>
          </div>
          <div className="pdp-pt-cell">
            <div className="pdp-pt-k">Hospital</div>
            <div className="pdp-pt-v">{inr(Math.round(product.price_min * 0.78))}</div>
          </div>
        </div>
      </div>

      <div>
        <div className="kicker" style={{ marginBottom: 8 }}>Pack size</div>
        <div className="pdp-variants">
          {variants.map((v, i) => (
            <button key={i} className={"pdp-variant" + (variant === i ? " is-active" : "")} onClick={() => setVariant(i)}>
              <strong>{v.label}</strong>
              <span>{v.sub} · {inr(v.price)}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="pdp-actions">
        <Quantity value={qty} onChange={setQty} />
        <button className="btn btn-primary btn-lg" onClick={handleAdd} disabled={addToCart.isPending || product.stock_status === "out_of_stock"}>
          <Icons.Plus size={16} stroke={2.2} /> {addToCart.isPending ? "Adding…" : "Add to cart"}
        </button>
        <button className="btn btn-outline btn-lg" onClick={handleBuyNow} disabled={addToCart.isPending || product.stock_status === "out_of_stock"}>Buy now</button>
        <button
          className="icon-btn"
          aria-label="Wishlist"
          onClick={toggleWish}
          style={wished ? { color: "#e11d48", borderColor: "#e11d48" } : undefined}
        >
          <Icons.Heart size={18} stroke={wished ? 2.4 : 1.7} />
        </button>
        <a className="icon-btn" href="/pages/storefront/compare.html" aria-label="Compare"><Icons.Hash size={18} /></a>
      </div>

      <div className="pdp-trust-row">
        <div className="pdp-trust-item">
          <Icons.Truck size={18} stroke={1.6} />
          <div>
            <strong>Same-day dispatch</strong>
            <span>Order before 4 PM IST</span>
          </div>
        </div>
        <div className="pdp-trust-item">
          <Icons.ShieldCheck size={18} stroke={1.6} />
          <div>
            <strong>Authentic & traceable</strong>
            <span>Batch-traceable from manufacturer</span>
          </div>
        </div>
        <div className="pdp-trust-item">
          <Icons.BadgeCheck size={18} stroke={1.6} />
          <div>
            <strong>CDSCO-licensed</strong>
            <span>WHO-GDP audited supply lane</span>
          </div>
        </div>
        <div className="pdp-trust-item">
          <Icons.Package size={18} stroke={1.6} />
          <div>
            <strong>Return within 7 days</strong>
            <span>Damage-on-delivery covered</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PDPTabs({ product }) {
  const [tab, setTab] = pdpUS("description");
  const tabs = [
    { k: "description", label: "Description" },
    { k: "specs",       label: "Specifications" },
    { k: "reviews",     label: "Reviews (" + (product.reviews || 0).toLocaleString("en-IN") + ")" },
    { k: "shipping",    label: "Shipping & returns" },
  ];
  return (
    <section className="pdp-tabs-section">
      <div className="container">
        <div className="pdp-tabs">
          {tabs.map((t) => (
            <button key={t.k} className={"pdp-tab" + (tab === t.k ? " is-active" : "")} onClick={() => setTab(t.k)}>{t.label}</button>
          ))}
        </div>

        {tab === "description" && (
          <div className="pdp-tab-content">
            <p>
              {product.name} is supplied by {product.brand}, a CDSCO-licensed manufacturer with a long-standing record in the Indian market. Each pack is sealed at source, scanned into our bonded warehouse against a unique batch identifier, and held in conditions that match the storage label printed on the carton.
            </p>
            <p style={{ marginTop: 12 }}>
              When you order, the unit is picked from the same batch we received from the manufacturer — we never repackage or comingle inventory across batches. The batch identifier and expiry are printed on your invoice and can be looked up against our traceability ledger at any time.
            </p>
            <p style={{ marginTop: 12 }}>
              Common uses, dosage and contraindications follow the product's approved label. Always read the leaflet inside the pack and consult a clinician before initiating, modifying or discontinuing therapy. This product is not intended for self-diagnosis.
            </p>
          </div>
        )}

        {tab === "specs" && (
          <dl className="pdp-specs">
            {product.specs.map((s, i) => (
              <div className="pdp-spec-row" key={i}>
                <dt>{s.label}</dt><dd>{s.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {tab === "reviews" && (
          <div className="review-grid">
            <div className="review-summary">
              <div className="review-summary-num">{product.rating.toFixed(1)}</div>
              <StarRating value={product.rating} size={18} />
              <div className="kicker">{(product.reviews || 0).toLocaleString("en-IN")} verified reviews</div>
              <div className="review-bars">
                {[5,4,3,2,1].map((star) => {
                  const pct = star === 5 ? 72 : star === 4 ? 20 : star === 3 ? 5 : star === 2 ? 2 : 1;
                  return (
                    <div key={star} className="review-bar">
                      <span className="mono">{star}</span>
                      <div className="review-bar-track"><div className="review-bar-fill" style={{ width: pct + "%" }} /></div>
                      <span className="mono">{pct}%</span>
                    </div>
                  );
                })}
              </div>
              <ReviewForm slug={product.slug} />
            </div>
            <ReviewsBlock slug={product.slug} />
          </div>
        )}

        {tab === "shipping" && (
          <div className="pdp-tab-content">
            <h3 style={{ fontSize: 18, marginBottom: 12 }}>Shipping</h3>
            <p>Orders placed before 4 PM IST are dispatched the same business day from our Palayamkottai (Tirunelveli, Tamil Nadu) warehouse. We ship to 50 cities directly and to all serviceable PINs via partner couriers. {product.category_slug === "cold-chain" ? "This is a cold-chain item — it ships under a validated 2–8°C lane with a Bluetooth temperature logger attached. Delivery may take up to 48 hours longer than standard lines." : "Typical delivery is 1–4 business days depending on lane and PIN."}</p>
            <h3 style={{ fontSize: 18, margin: "24px 0 12px" }}>Returns</h3>
            <p>
              Returnable within 7 days of delivery, original sealed pack. {product.schedule === "H" ? "As a Schedule H drug, returns are limited to damage or quality reasons, in line with the Drugs & Cosmetics Act." : "All damage claims are credited to your wallet within 48 hours; full refund settlements clear to source bank in 5–7 working days."} See our <a href="/pages/policy/refund.html" style={{ color: "#0a0a0a", fontWeight: 500 }}>full refund policy</a>.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

function ReviewsBlock({ slug }) {
  const { data, isLoading } = useReviews(slug, { limit: 20 });
  return <ReviewList items={data?.items ?? []} loading={isLoading} />;
}

function PDPRelated({ product }) {
  const { data } = useProducts({ category: product.category_slug, limit: 5 });
  const related = (data?.items ?? []).filter((p) => p.slug !== product.slug).slice(0, 4);
  if (!related.length) return null;
  return (
    <section className="section" style={{ paddingTop: 0 }}>
      <div className="container">
        <div className="section-head">
          <div>
            <div className="section-eyebrow"><span className="bar" /><span className="kicker">Related · same category</span></div>
            <h2 className="section-title" style={{ fontSize: "clamp(22px, 2.4vw, 28px)" }}>You may also need</h2>
          </div>
          <div className="right">
            <a href={"/pages/storefront/category.html?slug=" + product.category_slug} className="btn btn-outline btn-sm">
              See more <Icons.ArrowR size={13} stroke={2} />
            </a>
          </div>
        </div>
        <div className="prod-grid cols-4">
          {related.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </div>
    </section>
  );
}

function ProductDetailPage() {
  const slug = getQuery("slug");
  const { data: apiProduct, isLoading, isError } = useProduct(slug);

  // Fallback to mock catalogue only if the API errors/empty AND the slug exists
  // there. Keeps design-review experience working when the backend is down.
  const mockProduct = slug ? NETHRA_DATA.products.find((p) => p.slug === slug) : null;
  const product = apiProduct || (isError ? mockProduct : null) || mockProduct;

  if (!product && isLoading) {
    return (
      <div className="container" style={{ padding: 80, textAlign: "center", color: "var(--ink-3)" }}>
        Loading product…
      </div>
    );
  }
  if (!product) {
    return (
      <div className="container" style={{ padding: 80, textAlign: "center" }}>
        <h2 style={{ marginBottom: 12 }}>Product not found</h2>
        <a className="btn btn-primary" href="/pages/storefront/products.html">Back to catalog</a>
      </div>
    );
  }
  return (
    <>
      <Breadcrumbs items={[
        { label: "Products", href: "/pages/storefront/products.html" },
        { label: product.category_name, href: "/pages/storefront/category.html?slug=" + product.category_slug },
        { label: product.name },
      ]} />
      <div className="container">
        <div className="pdp-layout">
          <PDPGallery product={product} />
          <PDPInfo product={product} />
        </div>
      </div>
      <PDPTabs product={product} />
      <PDPRelated product={product} />
    </>
  );
}

export { ProductDetailPage };
